import { Badge, Button, Modal, Select } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PurchaseOrderListItem, PurchaseOrderStatus } from '@warehouse/shared';
import { Plus } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { DataTable, type DataTableColumn } from '../../../components/DataTable';
import { PageHeader } from '../../../components/PageHeader';
import { useListState } from '../../../hooks/useListState';
import { usePermission } from '../../../auth/auth-store';
import { formatDate, formatDateTime } from '../../../lib/format';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import { purchaseOrdersApi, receivingKeys } from '../api';
import { PO_STATUS_TONE } from '../format';
import { PurchaseOrderForm } from './PurchaseOrderForm';

const STATUSES: PurchaseOrderStatus[] = [
  'open',
  'partially_received',
  'received',
  'closed',
  'cancelled',
];

/** `/orders/purchase` — spec §5.2. */
export function PurchaseOrdersPage(): ReactNode {
  const { t } = useTranslation('receiving');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canCreate = usePermission('orders.inbound.create');
  const [creating, setCreating] = useState(false);

  const { state, setState, setFilter } = useListState({ defaultSort: 'createdAt', defaultDir: 'desc' });

  const query = useQuery({
    queryKey: receivingKeys.purchaseOrders(state),
    queryFn: () => purchaseOrdersApi.list(state),
  });
  useLiveQuery(['purchase-orders'], ['purchase-orders']);

  const create = useMutation({
    mutationFn: purchaseOrdersApi.create,
    onSuccess: async (result) => {
      toastSuccess(t('po.form.saved'));
      setCreating(false);
      await queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      void navigate(`/orders/purchase/${result.poId}`);
    },
    onError: (error) => {
      toastError(error);
    },
  });

  const columns: DataTableColumn<PurchaseOrderListItem>[] = [
    {
      id: 'poNo',
      header: t('po.poNo'),
      sortField: 'poNo',
      cell: (row) => <span className="font-mono text-ink">{row.poNo}</span>,
    },
    {
      id: 'supplier',
      header: t('po.supplier'),
      sortField: 'supplierName',
      cell: (row) => row.supplierName,
    },
    {
      id: 'status',
      header: t('po.status'),
      sortField: 'status',
      cell: (row) => (
        <Badge tone={PO_STATUS_TONE[row.status]} dot minWidth>
          {t(`po.statuses.${row.status}`)}
        </Badge>
      ),
    },
    {
      id: 'expectedDate',
      header: t('po.expectedDate'),
      sortField: 'expectedDate',
      hideBelow: 'md',
      cell: (row) => formatDate(row.expectedDate),
    },
    {
      id: 'lines',
      header: t('po.lineCount'),
      align: 'right',
      hideBelow: 'lg',
      cell: (row) => row.lineCount,
    },
    {
      id: 'progress',
      header: t('po.progress'),
      align: 'right',
      cell: (row) => <ProgressCell pct={row.receivedPct} />,
    },
    {
      id: 'createdAt',
      header: t('po.createdAt'),
      sortField: 'createdAt',
      hideBelow: 'xl',
      cell: (row) => formatDateTime(row.createdAt),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('po.title')}
        subtitle={t('po.subtitle')}
        actions={
          canCreate ? (
            <Button
              icon={<Plus className="size-4" />}
              onClick={() => {
                setCreating(true);
              }}
            >
              {t('po.new')}
            </Button>
          ) : null
        }
      />

      <DataTable
        columns={columns}
        rows={query.data?.items ?? []}
        rowKey={(row) => row.id}
        state={state}
        onStateChange={setState}
        total={query.data?.total ?? null}
        loading={query.isLoading}
        error={query.error}
        searchPlaceholder={t('po.searchPlaceholder')}
        emptyTitle={t('po.empty')}
        emptyDescription={t('po.emptyHint')}
        onRowClick={(row) => {
          void navigate(`/orders/purchase/${row.id}`);
        }}
        filters={
          <Select
            aria-label={t('po.filterStatus')}
            options={[
              { value: '', label: t('po.filterStatus') },
              ...STATUSES.map((status) => ({ value: status, label: t(`po.statuses.${status}`) })),
            ]}
            value={String(state.filters?.status ?? '')}
            onChange={(event) => {
              setFilter('status', event.target.value || undefined);
            }}
          />
        }
      />

      {creating ? (
        <Modal open onClose={() => { setCreating(false); }} title={t('po.form.title')} width={720}>
          <PurchaseOrderForm
            busy={create.isPending}
            onCancel={() => {
              setCreating(false);
            }}
            onSubmit={(input) => {
              create.mutate(input);
            }}
          />
        </Modal>
      ) : null}
    </>
  );
}

function ProgressCell({ pct }: { pct: number }): ReactNode {
  return (
    <div className="flex items-center justify-end gap-2">
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-surface-subtle" aria-hidden>
        <div className="h-full rounded-full bg-brand-600" style={{ width: `${pct}%` }} />
      </div>
      <span className="w-10 text-right tabular-nums text-ink-muted">{pct}%</span>
    </div>
  );
}
